import { EAutreCgpGroups } from "common/enums/Scpi/PhysicalPerson/Groups/EAutreCgpGroups";
import { EConnaissanceExprerienceGroups } from "common/enums/Scpi/PhysicalPerson/Groups/EConnaissanceExprerienceGroups";
import { EFinanceDurableGroups } from "common/enums/Scpi/PhysicalPerson/Groups/EFinanceDurableGroups";
import { EPatrimoineRevenusGroups } from "common/enums/Scpi/PhysicalPerson/Groups/EPatrimoineRevenusGroups";
import { ETestAdequationGroups } from "common/enums/Scpi/PhysicalPerson/Groups/ETestAdequationGroups";
import GenericAutreCgpRequestResource from "common/resources/Scpi/PhysicalPerson/GenericAutreCgpRequestResource";
import GenericConnaissanceExperienceRequestResource from "common/resources/Scpi/PhysicalPerson/GenericConnaissanceExperienceRequestResource";
import GenericFinanceDurableRequestResource from "common/resources/Scpi/PhysicalPerson/GenericFinanceDurableRequestResource";
import GenericPatrimoineRequestResource from "common/resources/Scpi/PhysicalPerson/GenericPatrimoineRequestResource";
import { GenericProduct } from "common/resources/Scpi/PhysicalPerson/GenericProduct";
import GenericTestAdequationRequestResource from "common/resources/Scpi/PhysicalPerson/GenericTestAdequationRequestResource";
import { getProductsAggregate, TProductName } from "common/resources/Scpi/PhysicalPerson/Products";
import SubscriptionResponseResource from "common/resources/Subscription/SubscriptionResponseResource";
import Button, { EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography, { ITypo } from "components/elements/Typography";
import I18n from "components/materials/I18n";
import InvestorPortalOrigin from "components/materials/InvestorPortalOrigin";
import ModuleConfig from "configs/ModuleConfig";
import useOpenable from "hooks/useOpenable";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import SubscriptionService from "services/SubscriptionService";
import * as P from "ts-pattern";
import { container } from "tsyringe";
import NumberUtils from "utils/NumberUtils";
import SubscriptionQuestionsStepper from "./Stepper";
import classes from "./classes.module.scss";
import ValidationDocumentsStepper, { IDocumentValidationSteps } from "components/pages/Subscriptions/physicalPerson/SubscriptionQuestions/ValidationDocumentsStepper";
import { EDocumentValidationStatus } from "common/enums/Subscription/EDocumentValidationStatus";
import { ESubscriptionProductType } from "common/enums/ProductSubscribed/ESubscriptionProductType";
import { EDemembrementType } from "common/enums/ProductSubscribed/EDemembrementType";
import { getViagerRatio } from "utils/baremeViager";
import SubscriptionLightUpdateRequestResource from "common/resources/Subscription/SubscriptionLightUpdateRequestResource";
import { ESubscriptionType } from "common/enums/Subscription/ESubscriptionType";
import ClientService from "services/ClientService";
import ClientSheetResponseResource from "common/resources/ClientSheet/ClientSheetResponseResource";
import EditClientModal from "../modals/EditClientModal";
import { ESubFilesTypes } from "common/enums/Subscription/File/ESubFilesTypes";
import { isPrivateEquityOnly, PERCEPTION_RIB_FILES } from "common/utils/privateEquity";
import { isProductEligibleForElectronicSignature } from "utils/signatureUtils";

type ISubscriptionSteps = {
	category: keyof GenericProduct;
	path: string;
	title: string;
};

export type ISubscriptionOutletContext = {
	productAggregate: GenericProduct;
	getInputsFromProductAggregate: (
		inputList:
			| Record<EPatrimoineRevenusGroups, JSX.Element | null>
			| Record<EConnaissanceExprerienceGroups, JSX.Element | null>
			| Record<ETestAdequationGroups, JSX.Element | null>
			| Record<EAutreCgpGroups, JSX.Element | null>
			| Record<EFinanceDurableGroups, JSX.Element | null>,
	) => JSX.Element[];
	updateSubscription: (
		resource:
			| GenericPatrimoineRequestResource
			| GenericConnaissanceExperienceRequestResource
			| GenericTestAdequationRequestResource
			| GenericFinanceDurableRequestResource
			| GenericAutreCgpRequestResource,
	) => void;
	updateConditionsSubscription: (resource: SubscriptionLightUpdateRequestResource) => void;
	subscription: SubscriptionResponseResource;
	goToNextStep: () => void;
	canSendSubscription: boolean;
	loadSubscription: () => void;
	sendSubcriptionToMo: () => void;
	setSubscriptionHasUnsavedChangesToTrue: (e: React.ChangeEvent<HTMLFormElement>) => void;
	setSubscriptionHasUnsavedChangesToFalse: () => void;
	currentSelectedPath: string;
	setCurrentSelectedPath: (path: string) => void;
	subscriptionWithKnowledge: SubscriptionResponseResource;
	setCanEnableDocumentStep: (value: boolean) => void;
	canEnableDocumentStep: boolean;
	documentValidationSteps: IDocumentValidationSteps[];
};

const subscriptionService = container.resolve(SubscriptionService);
const clientService = container.resolve(ClientService);
const modules = container.resolve(ModuleConfig).get().modules;

function getProductSubscribedAmount(productSubscribed: NonNullable<SubscriptionResponseResource["productsSubscribed"]>[number]): number {
	const { subscriptionProductType, demembrementDuration, demembrementType, viagerAge, quantity, productSheet } = productSubscribed;
	const basePrice = quantity * productSheet!.price;
	if (!subscriptionProductType || subscriptionProductType === ESubscriptionProductType.pleine_propriete) return basePrice;
	if (demembrementType === EDemembrementType.viager && viagerAge != null) {
		return basePrice * (getViagerRatio(viagerAge, subscriptionProductType) ?? 1);
	}
	if (demembrementDuration) {
		const key = productSheet?.demembrementKeys?.find((k) => k.duration === demembrementDuration);
		if (key) return basePrice * (subscriptionProductType === ESubscriptionProductType.usufruit ? key.usufruitRatio : key.nueProprieteRatio);
	}
	return basePrice;
}

const subscriptionPages = modules.pages.Subscriptions.props.pages.SubscriptionQuestions.pages;
const stepByCategory: ISubscriptionSteps[] = [
	{
		category: "payment_methods",
		path: subscriptionPages.PaymentMethods.props.path,
		title: "Règlement",
	},
	{
		category: "patrimoine_revenus",
		path: subscriptionPages.PatrimoineRevenus.props.path,
		title: "Patrimoine & revenus",
	},
	{
		category: "connaissance_experience",
		path: subscriptionPages.ConnaissanceExperience.props.path,
		title: "Connaissance & expérience",
	},
	{
		category: "finance_durable",
		path: subscriptionPages.FinanceDurable.props.path,
		title: "Finance durable",
	},
	{
		category: "test_adequation",
		path: subscriptionPages.TestAdequation.props.path,
		title: "Test d'adéquation",
	},
	{
		category: "autre_cgp",
		path: subscriptionPages.AutreCgp.props.path,
		title: "Appréciation sur l'opération",
	},
	{
		category: "files",
		path: subscriptionPages.Files.props.path,
		title: "Dépôt des documents",
	},
];

export default function SubscriptionQuestions() {
	const subscriberModal = useOpenable();
	const coSubscriberModal = useOpenable();
	const { subscriptionId } = useParams();
	const [productAggregate, setProductAggregate] = useState<GenericProduct | null>(null);
	const [actualCategory, setActualCategory] = useState<keyof GenericProduct | null>(null);
	const [subscription, setSubscription] = useState<SubscriptionResponseResource | null>(null);
	const [subscriptionWithKnowledge, setSubscriptionWithKnowledge] = useState<SubscriptionResponseResource | null>(null);
	const [canSendSubscription, setCanSendSubscription] = useState(false);
	const [subscriptionHasUnsavedChanges, setSubscriptionHasUnsavedChanges] = useState<boolean>(false);
	const [currentSelectedPath, setCurrentSelectedPath] = useState<string>("");
	const [currentFormElement, setCurrentFormElement] = useState<HTMLFormElement | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [validateDocumentsStepEnabled, setValidateDocumentsStepEnabled] = useState(false);
	const [hasTutoModalBeenOpened, setHasTutoModalBeenOpened] = useState(false);
	const [canEnableDocumentStep, setCanEnableDocumentStep] = useState(false);
	const [shouldTriggerDocumentResetWarning, setShouldTriggerDocumentResetWarning] = useState(false);
	const { isOpen: isOpenResetWarningModal, open: openResetWarningModal, close: closeResetWarningModal } = useOpenable();
	const [pendingSubmitCallback, setPendingSubmitCallback] = useState<(() => void) | null>(null);
	const [subscriber, setSubscriber] = useState<ClientSheetResponseResource | null>(null);
	const [coSubscriber, setCoSubscriber] = useState<ClientSheetResponseResource | null>(null);
	const isCoSubscription = subscription?.subscriptionType === ESubscriptionType.coSubscriptionNaturalPerson;

	const filteredSteps = useMemo(
		() =>
			stepByCategory
				.filter((step) => !!productAggregate?.[step.category]?.groups.length)
				.map((step) => ({ ...step, path: step.path.replace(":subscriptionId", subscriptionId ?? "") })),
		[productAggregate, subscriptionId],
	);

	const requiresElectronicSignature = useMemo(() => {
		return subscription?.productsSubscribed?.some(isProductEligibleForElectronicSignature) ?? false;
	}, [subscription]);

	const { isOpen: isOpenUnsavedChangeModal, close: closeUnsavedChangeModal, open: openUnsavedChangeModal } = useOpenable();
	const { isOpen: isOpenValidatedModal, close: closeValidatedModal, open: openValidatedModal } = useOpenable();
	const { isOpen: isOpenSignDocumentTutoModal, close: closeSignDocumentTutoModal, open: openSignDocumentTutoModal } = useOpenable();

	const navigate = useNavigate();
	const location = useLocation();

	const checkCategoryIsCompleted = useCallback(
		(category: keyof GenericProduct) => {
			if (!subscription) return false;
			return P.match(category)
				.with("autre_cgp", () => {
					return !!subscription.autreCGP;
				})
				.with("patrimoine_revenus", () => {
					return !!subscription.patrimoineRevenus;
				})
				.with("connaissance_experience", () => {
					return !!subscription.connaissanceExperience;
				})
				.with("test_adequation", () => {
					return !!subscription.testAdequation;
				})
				.with("finance_durable", () => {
					return !!subscription.financeDurable;
				})
				.with("payment_methods", () => {
					return !!(subscription.fundsOrigin && subscription.financialObjective && subscription.fundsOrigin.length >= 1 && subscription.financialObjective.length >= 1);
				})
				.with("files", () => {
					if (!productAggregate) return false;
					const productSubscribedHasFiles = subscription.productsSubscribed?.every((product) => !!product.productSubscribedToFiles) ?? false;
					if (!productSubscribedHasFiles) return false;
					let hasAllFiles = true;
					const withoutPerceptionRib = isPrivateEquityOnly(subscription.productsSubscribed);
					productAggregate.files.groups.forEach((file) => {
						if (withoutPerceptionRib && PERCEPTION_RIB_FILES.includes(file)) return;
						if (file === ESubFilesTypes.DERNIER_RELEVE_COMPTE) return;
						if (file === ESubFilesTypes.OTHERS) return;
						if (file === ESubFilesTypes.RIB_PRELEVEMENT) return;
						subscription.productsSubscribed?.forEach((product) => {
							if (!product.productSubscribedToFiles?.find((subFile) => subFile.type === file)) hasAllFiles = false;
						});
					});
					return hasAllFiles;
				})
				.exhaustive();
		},
		[productAggregate, subscription],
	);

	// Pas de convention RTO en private equity : la validation commence par les documents du produit.
	const privateEquityOnly = useMemo(() => isPrivateEquityOnly(subscription?.productsSubscribed), [subscription]);

	const documentValidationSteps: IDocumentValidationSteps[] = useMemo(() => {
		return [
			...(privateEquityOnly
				? []
				: [
						{
							status: subscription?.rtoDocumentsStatus ?? EDocumentValidationStatus.pending,
							name: "Convention RTO",
							path: modules.pages.Subscriptions.props.pages.SubscriptionQuestions.pages.ValidateRto.props.path.replace(":subscriptionId", subscriptionId ?? ""),
						},
					]),
			...(subscription?.productsSubscribed
				?.map((productSubscribed) => ({
					status: productSubscribed.documentsValidatedStatus ?? EDocumentValidationStatus.pending,
					name: productSubscribed.productSheet?.name ?? "",
					path: modules.pages.Subscriptions.props.pages.SubscriptionQuestions.pages.ValidateDocuments.props.path
						.replace(":subscriptionId", subscriptionId ?? "")
						.replace(":productSubscribedId", productSubscribed.id),
				}))
				.sort((a, b) => a.name.localeCompare(b.name)) ?? []),
		];
	}, [subscription, subscriptionId, privateEquityOnly]);

	const checkValidationDocumentIsCompleted = useCallback((status: EDocumentValidationStatus) => {
		return status === EDocumentValidationStatus.validated;
	}, []);

	const isInDocumentValidationStep = useMemo(() => {
		return documentValidationSteps.some((step) => matchPath(step.path, location.pathname));
	}, [documentValidationSteps, location.pathname]);

	const updateCanSendSubscription = useCallback(() => {
		if (!productAggregate) return;
		const isValid = filteredSteps.every((step) => checkCategoryIsCompleted(step.category));
		setCanSendSubscription(isValid);
	}, [checkCategoryIsCompleted, productAggregate, filteredSteps]);

	const goToNextStep = useCallback(() => {
		// Get in the stepByCategory object the key that has the value of the currentStep
		const actualStepKey = filteredSteps.findIndex((step) => step.category === actualCategory) ?? 0;
		const nextStep = filteredSteps?.[actualStepKey + 1];

		if (!nextStep) return;
		return navigate(nextStep.path.replace(":subscriptionId", subscriptionId as string));
	}, [actualCategory, navigate, subscriptionId, filteredSteps]);

	const getInputsFromProductAggregate = useCallback(
		(
			inputList:
				| Record<EPatrimoineRevenusGroups, JSX.Element>
				| Record<EConnaissanceExprerienceGroups, JSX.Element>
				| Record<ETestAdequationGroups, JSX.Element>
				| Record<EAutreCgpGroups, JSX.Element>
				| Record<EFinanceDurableGroups, JSX.Element>,
		) => {
			if (!productAggregate || !actualCategory) return;
			const inputs: JSX.Element[] = [];
			// Get the keys of the inputs, required in order to keep the order of the inputs
			const inputsKeys = Object.keys(inputList);
			inputsKeys.forEach((key) => {
				// Get the key of the inputList and check if it exists in the productAggregate, if so, push the input to the inputs array
				if (productAggregate[actualCategory]?.groups.find((question) => question === key)) inputs.push(inputList[key as keyof typeof inputList]);
			});
			return inputs;
		},
		[actualCategory, productAggregate],
	);

	const loadSubscription = useCallback(async () => {
		if (!subscriptionId) return navigate(modules.pages.Home.props.path);
		await subscriptionService.syncDraftWithLastProductSheet(subscriptionId);
		const subscription = await subscriptionService.getByUid(subscriptionId);
		if (!subscription || !subscription.productsSubscribed || !subscription.productsSubscribed[0]?.subscriberId) return navigate(modules.pages.Home.props.path);
		setSubscription(subscription);
		setProductAggregate(getProductsAggregate(subscription.productsSubscribed!.map((product) => product.productSheet!.product?.referenceName as TProductName)));
		const clientKnowledge = await subscriptionService.getClientKnowledge(subscription.productsSubscribed[0].subscriber?.clientId!);
		const subscriptionWithKnowledge = _.mergeWith({ ...clientKnowledge }, subscription, (objValue: any, srcValue: any) => {
			if (srcValue === null) {
				return objValue; // Retourne la valeur originale, ignorant la valeur null
			}
		});
		setSubscriptionWithKnowledge(subscriptionWithKnowledge);
		setIsLoading(false);
	}, [navigate, subscriptionId]);

	const updateSubscription = useCallback(
		async (
			resource:
				| GenericPatrimoineRequestResource
				| GenericConnaissanceExperienceRequestResource
				| GenericTestAdequationRequestResource
				| GenericFinanceDurableRequestResource
				| GenericAutreCgpRequestResource,
		) => {
			const proceed = async () => {
				await subscriptionService
					.updateCategory(resource, subscriptionId as string, actualCategory as keyof GenericProduct)
					.then(async (_subscription) => {
						loadSubscription();
						setSubscriptionHasUnsavedChanges(false);
						setShouldTriggerDocumentResetWarning(false);
						setPendingSubmitCallback(null);

						// Désactiver la vérification des documents
						setCanEnableDocumentStep(false);
						await subscriptionService.resetSubscriptionDocumentsStatus(subscriptionId as string);

						if (currentSelectedPath !== "") {
							navigate(currentSelectedPath);
							setCurrentSelectedPath("");
							return;
						}
						goToNextStep();
					})
					.catch((err) => {
						setCurrentSelectedPath("");
						console.error(err);
					});
			};

			if (shouldTriggerDocumentResetWarning) {
				setPendingSubmitCallback(() => proceed);
				openResetWarningModal();
				return;
			}

			await proceed();
		},
		[
			subscriptionId,
			actualCategory,
			loadSubscription,
			currentSelectedPath,
			goToNextStep,
			navigate,
			shouldTriggerDocumentResetWarning,
			openResetWarningModal,
			setCanEnableDocumentStep,
		],
	);

	const updateConditionsSubscription = useCallback(
		async (resource: SubscriptionLightUpdateRequestResource) => {
			await subscriptionService
				.updateConditionsInformations(resource)
				.then(() => {
					loadSubscription();
				})
				.catch((err) => {
					console.error(err);
				});
		},
		[loadSubscription],
	);

	const sendSubcriptionToMo = useCallback(async () => {
		if (!subscriptionId) return;
		if (validateDocumentsStepEnabled) {
			navigate(modules.pages.Subscriptions.props.pages.SubscriptionQuestions.pages.ValidateRto.props.path.replace(":subscriptionId", subscriptionId));
		} else {
			await subscriptionService.submit(subscriptionId);
			openValidatedModal();
		}
	}, [subscriptionId, navigate, openValidatedModal, validateDocumentsStepEnabled]);

	const onValidatedModalClose = useCallback(() => {
		closeValidatedModal();
		navigate(modules.pages.Subscriptions.props.pages.WaitingSignature.props.path);
	}, [closeValidatedModal, navigate]);

	const onCloseUnsavedChangeModal = useCallback(() => {
		closeUnsavedChangeModal();
		setCurrentSelectedPath("");
	}, [closeUnsavedChangeModal]);

	const continueWithoutSaving = useCallback(() => {
		closeUnsavedChangeModal();
		setSubscriptionHasUnsavedChanges(false);
		navigate(currentSelectedPath);
	}, [currentSelectedPath, navigate, closeUnsavedChangeModal]);

	const onModalConfirmUnsavedChange = useCallback(() => {
		closeUnsavedChangeModal();
		const event = new Event("submit", { bubbles: true });
		currentFormElement && currentFormElement.dispatchEvent(event);
	}, [closeUnsavedChangeModal, currentFormElement]);

	const onClickedLink = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, currentStepPath: string) => {
			if (!subscriptionHasUnsavedChanges) return;
			e.preventDefault();
			setCurrentSelectedPath(currentStepPath);
			openUnsavedChangeModal();
		},
		[subscriptionHasUnsavedChanges, openUnsavedChangeModal],
	);

	const onClickedValidationDocumentLink = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, targetPath: string) => {
			if (subscriptionHasUnsavedChanges) {
				e.preventDefault();
				setCurrentSelectedPath(targetPath);
				openUnsavedChangeModal();
			} else {
				e.preventDefault();
				navigate(targetPath);
			}
		},
		[subscriptionHasUnsavedChanges, navigate, openUnsavedChangeModal],
	);

	const setSubscriptionHasUnsavedChangesToTrue = useCallback(
		(e: React.ChangeEvent<HTMLFormElement>) => {
			setCurrentFormElement(e.currentTarget);
			setSubscriptionHasUnsavedChanges(true);

			const currentStepIsEditable = actualCategory !== "files" && actualCategory !== null;

			if (currentStepIsEditable && canSendSubscription && requiresElectronicSignature) {
				setShouldTriggerDocumentResetWarning(true);
			}

			setCanEnableDocumentStep(false);
		},
		[actualCategory, canSendSubscription, requiresElectronicSignature],
	);

	const setSubscriptionHasUnsavedChangesToFalse = useCallback(() => {
		setSubscriptionHasUnsavedChanges(false);
	}, []);

	const getTranslatedCategory = useCallback(() => {
		const rtoDocumentPath = modules.pages.Subscriptions.props.pages.SubscriptionQuestions.pages.ValidateRto.props.path.replace(":subscriptionId", subscriptionId ?? "");

		if (matchPath(rtoDocumentPath, location.pathname)) {
			return I18n.asset.pages.subscriptions.validation_documents;
		}

		// Check if current path matches any product's document validation route
		const matchedProduct = subscription?.productsSubscribed?.find((product) => {
			const productPath = modules.pages.Subscriptions.props.pages.SubscriptionQuestions.pages.ValidateDocuments.props.path
				.replace(":subscriptionId", subscriptionId ?? "")
				.replace(":productSubscribedId", product.id);

			return matchPath(productPath, location.pathname);
		});

		if (matchedProduct) {
			return I18n.asset.pages.subscriptions.validation_documents;
		}

		// Fallback to actual subscription step
		return actualCategory ? I18n.asset.pages.subscriptions[actualCategory] : I18n.asset.pages.subscriptions.summary;
	}, [actualCategory, subscriptionId, location.pathname, subscription]);

	const fetchSubscriber = useCallback(async () => {
		if (!subscription) return;
		const subscriberId = subscription.productsSubscribed?.[0]?.subscriberId;
		if (!subscriberId) return;
		clientService.getClientSheetById(subscriberId).then(setSubscriber);
	}, [subscription]);

	const fetchCoSubscriber = useCallback(async () => {
		if (!subscription) return;
		if (subscription.subscriptionType !== ESubscriptionType.coSubscriptionNaturalPerson) return;
		const productSubscribed = subscription.productsSubscribed?.[0];
		if (!productSubscribed) return;
		const coSubscriberId = subscription.productsSubscribed?.[0]?.subscriber?.spouseId;
		if (!coSubscriberId) return;
		clientService.getClientSheetById(coSubscriberId).then(setCoSubscriber);
	}, [subscription]);

	const updateToLastClientSheet = useCallback(async () => {
		if (!subscriptionId || !subscriber) return;
		subscriptionService.updateToLastClientSheet(subscriptionId, subscriber.clientId).then(() => {
			loadSubscription();
		});
	}, [subscriptionId, subscriber, loadSubscription]);

	const onEditSuccess = useCallback(async () => {
		subscriberModal.close();

		await updateToLastClientSheet();

		if (isCoSubscription) {
			coSubscriberModal.open();
		}
	}, [subscriberModal, coSubscriberModal, isCoSubscription, updateToLastClientSheet]);

	const onEditCoSubscriberSuccess = useCallback(async () => {
		coSubscriberModal.close();

		await updateToLastClientSheet();
	}, [coSubscriberModal, updateToLastClientSheet]);

	useEffect(() => {
		const signDocumentsPath = modules.pages.Subscriptions.props.pages.SubscriptionQuestions.pages.ValidateRto.props.path.replace(":subscriptionId", subscriptionId ?? "");
		if (matchPath(location.pathname, signDocumentsPath) && !hasTutoModalBeenOpened) {
			openSignDocumentTutoModal();
			setHasTutoModalBeenOpened(true);
		}
	}, [location.pathname, subscriptionId, hasTutoModalBeenOpened, openSignDocumentTutoModal]);

	useEffect(() => {
		if (!subscriptionId) return;
		(async () => {
			const eligibleSignatureAutomation = await subscriptionService.eligibleSignatureAutomation(subscriptionId);
			setValidateDocumentsStepEnabled(eligibleSignatureAutomation);
		})();
	}, [subscriptionId]);

	useEffect(() => {
		// each time the subscription is updated, check if the subscription is valid and can be sent
		updateCanSendSubscription();
	}, [updateCanSendSubscription, subscription]);

	useEffect(() => {
		setActualCategory(filteredSteps.find((step) => matchPath(step.path, location.pathname))?.category ?? null);
		// If the location matches the path of the root without any step, go to the next step, which means go to the first step
		if (!productAggregate) return;
		const match = matchPath(modules.pages.Subscriptions.props.pages.SubscriptionQuestions.props.path, location.pathname);
		if (!!match) return goToNextStep();
	}, [goToNextStep, location.pathname, productAggregate, filteredSteps]);

	useEffect(() => {
		if (!subscriptionId) return navigate(modules.pages.Home.props.path);
		loadSubscription();
	}, [loadSubscription, navigate, subscriptionId]);

	useEffect(() => {
		fetchSubscriber();
		fetchCoSubscriber();
	}, [fetchSubscriber, fetchCoSubscriber]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location]);

	return (
		<>
			{isLoading || !productAggregate || !subscription ? (
				<div>Chargement</div>
			) : (
				<I18n>
					<div className={classes["root"]}>
						<main className={classes["body"]}>
							{subscriptionId && (
								<SubscriptionQuestionsStepper
									subscriptionId={subscriptionId}
									checkCategoryIsCompleted={checkCategoryIsCompleted}
									onClickedLink={onClickedLink}
									signDocumentStepEnabled={validateDocumentsStepEnabled}
									signDocumentStepActive={canSendSubscription && (subscription.hasAcceptedConditions ?? false)}
									steps={filteredSteps}
								/>
							)}
							<div className={classes["content-right"]}>
								<Typography typo={ITypo.DISPLAY_MEDIUM} className={classes["title"]}>
									{getTranslatedCategory().page_title}
								</Typography>
								<div className={classes["content"]}>
									<Outlet
										context={
											{
												getInputsFromProductAggregate,
												productAggregate,
												updateSubscription,
												subscription,
												goToNextStep,
												canSendSubscription,
												loadSubscription,
												sendSubcriptionToMo,
												setSubscriptionHasUnsavedChangesToTrue,
												setSubscriptionHasUnsavedChangesToFalse,
												currentSelectedPath,
												setCurrentSelectedPath,
												subscriptionWithKnowledge,
												setCanEnableDocumentStep,
												canEnableDocumentStep,
												documentValidationSteps,
												updateConditionsSubscription,
											} as ISubscriptionOutletContext
										}
									/>
								</div>
							</div>
						</main>
						{/* Produits souscrits : visible tant que la page actuelle est avant 'validate_rto' */}
						{!isInDocumentValidationStep && (
							<div className={classes["top-recap"]}>
								<Typography typo={ITypo.P_LARGE_BOLD} className={classes["title"]}>
									Produits souscrits
								</Typography>
								<div className={classes["separator"]} />
								<div className={classes["subscribers-container"]}>
									<div className={classes["text-container"]}>
										<Typography typo={ITypo.P_MEDIUM_BOLD}>Souscripteur(s) :</Typography>
										<Button variant={EButtonVariant.TEXT} onClick={subscriberModal.open}>
											Modifier
										</Button>
									</div>
									<div className={classes["subscribers"]}>
										<Typography typo={ITypo.P_MEDIUM}>{`${subscriber?.firstName} ${subscriber?.lastName}`}</Typography>
										{coSubscriber && <Typography typo={ITypo.P_MEDIUM}>{`${coSubscriber.firstName} ${coSubscriber.lastName}`}</Typography>}
									</div>
								</div>
								<div className={classes["separator"]} />
								<div className={classes["products"]}>
									{subscription?.productsSubscribed?.map((productSubscribed) => (
										<div className={classes["product"]} key={productSubscribed.id}>
											<div className={classes["product-header"]}>
												<Typography typo={ITypo.P_MEDIUM_BOLD}>{productSubscribed.productSheet!.name}</Typography>
												<Typography typo={ITypo.P_MEDIUM_BOLD}>
													{NumberUtils.formatNumberAddingSpaces(getProductSubscribedAmount(productSubscribed))}€
												</Typography>
											</div>
											<div className={classes["product-infos"]}>
												<Typography typo={ITypo.P_MEDIUM}>{I18n.asset.enums.EPaymentMethod[productSubscribed.paymentMethod]}</Typography>
												<Typography typo={ITypo.P_MEDIUM}>{productSubscribed.quantity} parts</Typography>
											</div>
											<div className={classes["product-separator"]} />
										</div>
									))}
								</div>
								<InvestorPortalOrigin variant="detail" invitation={subscription.investorInvitation} />
							</div>
						)}

						{isInDocumentValidationStep && (
							<ValidationDocumentsStepper
								onClickedLink={onClickedValidationDocumentLink}
								checkValidationIsCompleted={checkValidationDocumentIsCompleted}
								steps={documentValidationSteps}
							/>
						)}
					</div>
				</I18n>
			)}
			<Modal isOpen={isOpenUnsavedChangeModal} onClose={onCloseUnsavedChangeModal} className={classes["modal-content"]}>
				<Typography typo={ITypo.H1}>{I18n.asset.common.warning}</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.subscriptions.unsaved_modifications}</Typography>
				<div className={classes["button-container"]}>
					<Button onClick={continueWithoutSaving} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.pages.subscriptions.continue_unsave}
					</Button>
					<Button onClick={onModalConfirmUnsavedChange}>{I18n.asset.common.save}</Button>
				</div>
			</Modal>
			<Modal isOpen={isOpenValidatedModal}>
				<div className={classes["modal-content"]}>
					<Typography typo={ITypo.H1} className={classes["modal-title"]}>
						{I18n.asset.pages.subscriptions.upload_documents.modal.title}
					</Typography>
					<Typography typo={ITypo.P_MEDIUM} className={classes["modal-description"]}>
						{I18n.asset.pages.subscriptions.upload_documents.modal.description}
						<a href={`mailto:${I18n.asset.pages.subscriptions.upload_documents.modal.address}`} rel="noreferrer" target="_blank">
							{I18n.asset.pages.subscriptions.upload_documents.modal.address}
						</a>
					</Typography>
					{subscription?.productsSubscribed?.some((p) => p.subscriptionProductType !== ESubscriptionProductType.pleine_propriete) && (
						<div className={classes["demembrement-note"]}>{I18n.asset.pages.subscriptions.upload_documents.modal.demembrement_note}</div>
					)}
					<Button onClick={onValidatedModalClose} className={classes["modal-button"]}>
						{I18n.asset.common.close}
					</Button>
				</div>
			</Modal>
			<Modal isOpen={isOpenSignDocumentTutoModal} className={classes["modal-tuto-root"]} style={{ maxWidth: "570px", width: "100%" }}>
				<Typography typo={ITypo.H1}>{I18n.asset.pages.subscriptions.validation_documents.modal.title}</Typography>
				<div className={classes["modal-tuto-content"]}>
					<Typography typo={ITypo.P_MEDIUM}>{I18n.asset.pages.subscriptions.validation_documents.modal.now}</Typography>
					<Typography typo={ITypo.P_MEDIUM} className={classes["underline"]}>
						{I18n.asset.pages.subscriptions.validation_documents.modal.next_step}
					</Typography>
					<div className={classes["modal-tuto-steps"]}>
						{!privateEquityOnly && <Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.subscriptions.validation_documents.modal.validate_convention}</Typography>}
						<Typography typo={ITypo.P_MEDIUM_BOLD}>
							{privateEquityOnly
								? I18n.asset.pages.subscriptions.validation_documents.modal.validate_documents_without_rto
								: I18n.asset.pages.subscriptions.validation_documents.modal.validate_documents}
						</Typography>
					</div>
					<Typography typo={ITypo.P_MEDIUM}>{I18n.asset.pages.subscriptions.validation_documents.modal.report}</Typography>
					<Typography typo={ITypo.P_MEDIUM}>
						{I18n.asset.pages.subscriptions.validation_documents.modal.question}
						<br />
						<a href={`mailto:${I18n.asset.pages.subscriptions.validation_documents.modal.address}`} rel="noreferrer" target="_blank">
							{I18n.asset.pages.subscriptions.validation_documents.modal.address}
						</a>
					</Typography>
				</div>
				<Button onClick={closeSignDocumentTutoModal}>{I18n.asset.pages.subscriptions.validation_documents.modal.close}</Button>
			</Modal>
			<Modal isOpen={isOpenResetWarningModal} onClose={closeResetWarningModal} className={classes["modal-content"]} style={{ maxWidth: "570px", width: "100%" }}>
				<Typography typo={ITypo.H1} className={classes["modal-title"]} style={{ marginBottom: "8px" }}>
					{I18n.asset.pages.subscriptions.reset_documents_modal.title}
				</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD} className={classes["modal-description"]} style={{ marginBottom: "8px" }}>
					{I18n.asset.pages.subscriptions.reset_documents_modal.message}
				</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.subscriptions.reset_documents_modal.warning_text}</Typography>

				<div className={classes["modal-button"]}>
					<Button onClick={closeResetWarningModal} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.common.cancel}
					</Button>
					<Button
						onClick={() => {
							closeResetWarningModal();
							setShouldTriggerDocumentResetWarning(false);

							if (pendingSubmitCallback) {
								pendingSubmitCallback();
							}
						}}>
						{I18n.asset.pages.subscriptions.reset_documents_modal.confirm_button}
					</Button>
				</div>
			</Modal>
			<EditClientModal
				isOpen={subscriberModal.isOpen}
				onClose={subscriberModal.close}
				onEditSuccess={onEditSuccess}
				clientToEdit={subscriber}
				title={I18n.asset.pages.subscriptions.existing_clients.modal_verify_client.title}
				subtitle={I18n.asset.pages.subscriptions.existing_clients.modal_verify_client.subtitle}
			/>
			<EditClientModal
				isOpen={coSubscriberModal.isOpen}
				onClose={coSubscriberModal.close}
				onEditSuccess={onEditCoSubscriberSuccess}
				clientToEdit={coSubscriber}
				title={I18n.asset.pages.subscriptions.existing_clients.modal_verify_client.title}
				subtitle={I18n.asset.pages.subscriptions.existing_clients.modal_verify_client.subtitle}
			/>
		</>
	);
}
